








































import { Component, Prop, Vue } from "vue-property-decorator";
import MultiSelect from "@/components/common/MultiSelect.vue";
import TDSSpinner from "@/components/common/TDSSpinner.vue";
import {
    DataPackageFilters,
    DataPackageCoverageFilter,
    DataPackageValidityFilter,
    DataPackageVolumeFilter,
    DataPackageCategoryFilter
} from "@/interfaces/httpResponses/GetDataPackageFiltersResponse";

/** Multi-value filter tokens emitted upward; the parent forwards them to the paged endpoint. */
export interface ActiveDataPackageFilters {
    countrySets?: string[];
    validityValues?: number[];
    sizeValues?: number[];
    includeUnlimited?: boolean;
    packageCategories?: string[];
}

@Component({
    components: { MultiSelect, TDSSpinner }
})
export default class ServerFilterPanel extends Vue {
    /** Facet options served by GET /products/data-packages/filters. */
    @Prop({ type: Object, default: null }) options!: DataPackageFilters | null;
    /** When true, group the coverage dropdown into Worldwide / Regions / Single Countries sections. */
    @Prop({ type: Boolean, default: false }) sectioned!: boolean;
    /** When true, the facets are still loading: the dropdowns are disabled and a spinner shows. */
    @Prop({ type: Boolean, default: false }) loading!: boolean;

    private selectedCoverage: string[] = [];
    private selectedValidity: string[] = [];
    private selectedVolume: string[] = [];
    private selectedCategory: string[] = [];

    get coverageItems() {
        const coverages = this.options?.coverages ?? [];
        const toItem = (c: DataPackageCoverageFilter) => ({
            val: c.countrySet,
            label: c.label || c.countrySet,
            countrySet: c.countrySet
        });
        if (!this.sectioned) {
            return coverages.map(toItem);
        }
        const sections: Array<{ type: string; title: string }> = [
            { type: "worldwide", title: "Worldwide" },
            { type: "region", title: "Regions" },
            { type: "single", title: "Single Countries" }
        ];
        const items: any[] = [];
        sections.forEach((section: { type: string; title: string }) => {
            const group = coverages.filter((c: DataPackageCoverageFilter) => c.coverageType === section.type);
            if (group.length === 0) return;
            items.push({ val: `__header_${section.type}`, label: section.title, isHeader: true });
            group.forEach((c: DataPackageCoverageFilter) => items.push(toItem(c)));
        });
        coverages
            .filter((c: DataPackageCoverageFilter) => !c.coverageType)
            .forEach((c: DataPackageCoverageFilter) => items.push(toItem(c)));
        return items;
    }

    get validityItems() {
        return (this.options?.validities ?? []).map((v: DataPackageValidityFilter) => ({
            val: `${v.validityValue}-${v.validityUnit}`,
            label: v.label || `${v.validityValue} ${v.validityUnit}`,
            validityValue: v.validityValue
        }));
    }

    get volumeItems() {
        return (this.options?.volumes ?? []).map((v: DataPackageVolumeFilter) => ({
            val: `${v.packageType}-${v.sizeValue ?? "x"}`,
            label: v.label || "",
            sizeValue: v.sizeValue,
            packageType: v.packageType
        }));
    }

    get categoryItems() {
        return (this.options?.categories ?? []).map((c: DataPackageCategoryFilter) => ({
            val: c.packageCategory,
            label: c.label || c.packageCategory
        }));
    }

    private onCoverageChange(vals: string[]) {
        this.selectedCoverage = vals;
        this.emitChange();
    }

    private onValidityChange(vals: string[]) {
        this.selectedValidity = vals;
        this.emitChange();
    }

    private onVolumeChange(vals: string[]) {
        this.selectedVolume = vals;
        this.emitChange();
    }

    private onCategoryChange(vals: string[]) {
        this.selectedCategory = vals;
        this.emitChange();
    }

    private emitChange() {
        const countrySets = [...this.selectedCoverage];

        const validityValues: number[] = [];
        this.selectedValidity.forEach((v: string) => {
            const item: any = this.validityItems.find((i: any) => i.val === v);
            if (item) validityValues.push(item.validityValue);
        });

        const sizeValues: number[] = [];
        let includeUnlimited = false;
        this.selectedVolume.forEach((v: string) => {
            const item: any = this.volumeItems.find((i: any) => i.val === v);
            if (!item) return;
            if (item.packageType === "unlimited") includeUnlimited = true;
            else if (item.sizeValue != null) sizeValues.push(item.sizeValue);
        });

        this.$emit("change", { countrySets, validityValues, sizeValues, includeUnlimited, packageCategories: [...this.selectedCategory] });
    }
}
